import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type { CalendarConfig } from "../api/types.js";
import { Card } from "../components/Card.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";

// US-centric IANA timezones, ordered roughly east-to-west so the
// list reads like a continental sweep. Add others as needed; the
// underlying input still accepts any IANA string via "Other".
const TIMEZONE_OPTIONS = [
  { value: "America/New_York",    label: "Eastern (America/New_York)" },
  { value: "America/Chicago",     label: "Central (America/Chicago)" },
  { value: "America/Denver",      label: "Mountain (America/Denver)" },
  { value: "America/Phoenix",     label: "Mountain — no DST (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
  { value: "America/Anchorage",   label: "Alaska (America/Anchorage)" },
  { value: "Pacific/Honolulu",    label: "Hawaii (Pacific/Honolulu)" },
  { value: "UTC",                 label: "UTC" },
];

const Schema = z.object({
  practice_timezone: z.string().min(1, "Required"),
  slot_granularity_minutes: z.coerce.number().int().positive(),
  min_lead_time_hours: z.coerce.number().int().min(0),
  max_lead_time_days: z.coerce.number().int().positive(),
});

type FormValues = z.infer<typeof Schema>;

// The weekly business-hours pattern is dead in this UI — day-to-day
// scheduling now lives on the Schedule calendar (per-provider shifts and
// blocks). The API still requires `business_hours` on PUT for backward
// compat, so it's tracked here in plain component state (never rendered,
// never user-editable) and sent back exactly as loaded.
//
// A config that doesn't exist yet has nothing to round-trip, so this is
// the fallback for the very first save. It stays Mon–Fri 09:00–17:00
// (the same default the removed textarea used to show), NOT all-empty.
// Patient availability (/api/v1/availability) no longer reads
// `business_hours` — `locationId` is required and slots come only from the
// Schedule. The pattern is still read by the Teams reschedule slot picker
// and the admin troubleshoot "computed availability" view, so an all-empty
// first save would silently zero those out with no UI left to fix it.
const DEFAULT_BUSINESS_HOURS: CalendarConfig["business_hours"] = {
  mon: [["09:00", "17:00"]],
  tue: [["09:00", "17:00"]],
  wed: [["09:00", "17:00"]],
  thu: [["09:00", "17:00"]],
  fri: [["09:00", "17:00"]],
  sat: [],
  sun: [],
};

type Props = { api: AdminApi };

export function CalendarConfigPage({ api }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The API OAuth callback still redirects to `/settings/calendar?google=connected` or
  // `?google=error` for historical reasons; the Google connection panel (and the banner
  // that reads these params) moved to ProvidersPage. Forward any `?google=` and optional
  // `?reason=` params to `/settings/providers` to show confirmation/error in the right place.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleParam = params.get("google");
    if (googleParam) {
      const newParams = new URLSearchParams();
      newParams.set("google", googleParam);
      const reasonParam = params.get("reason");
      if (reasonParam) {
        newParams.set("reason", reasonParam);
      }
      navigate(`/settings/providers?${newParams.toString()}`, { replace: true });
    }
  }, [location.search, navigate]);

  const [saved, setSaved] = useState(false);
  const [businessHours, setBusinessHours] = useState<
    CalendarConfig["business_hours"]
  >(DEFAULT_BUSINESS_HOURS);
  const q = useQuery({
    queryKey: ["admin", "calendar-config"],
    queryFn: () => api.getCalendarConfig(),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(Schema) });

  useEffect(() => {
    if (q.data) {
      setBusinessHours(q.data.business_hours);
      reset({
        practice_timezone: q.data.practice_timezone,
        slot_granularity_minutes: q.data.slot_granularity_minutes,
        min_lead_time_hours: q.data.min_lead_time_hours,
        max_lead_time_days: q.data.max_lead_time_days,
      });
    } else if (!q.isPending) {
      reset({
        practice_timezone: "",
        slot_granularity_minutes: 15,
        min_lead_time_hours: 24,
        max_lead_time_days: 60,
      });
    }
  }, [q.data, q.isPending, reset]);

  const submit = useMutation({
    mutationFn: (v: FormValues) =>
      api.putCalendarConfig({
        practice_timezone: v.practice_timezone,
        business_hours: businessHours,
        slot_granularity_minutes: v.slot_granularity_minutes,
        min_lead_time_hours: v.min_lead_time_hours,
        max_lead_time_days: v.max_lead_time_days,
      }),
    onSuccess: () => {
      setSaved(true);
      // SchedulePage reads practice_timezone off this same query key — a
      // stale cached value there would keep rendering the calendar (and
      // computing the appointments overlay's bounds) in the old timezone
      // until something else happened to refetch it.
      void queryClient.invalidateQueries({
        queryKey: ["admin", "calendar-config"],
      });
    },
  });

  return (
    <div className="grid gap-4">
      <Card title="Booking rules">
        {q.isError ? (
          <ErrorBanner message="Could not load configuration." />
        ) : null}
        {submit.isError ? (
          <ErrorBanner message={(submit.error as Error).message} />
        ) : null}
        {saved ? (
          <p className="mb-4 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
            Saved.
          </p>
        ) : null}
        <p className="mb-4 text-sm text-slate-600">
          Day-to-day schedules are managed on the Schedule calendar.
        </p>
        <form
          onSubmit={handleSubmit((v) => {
            setSaved(false);
            submit.mutate(v);
          })}
          className="grid gap-4"
        >
          <Select
            label="Practice timezone"
            required
            options={TIMEZONE_OPTIONS}
            {...register("practice_timezone")}
            error={errors.practice_timezone?.message}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Slot granularity (min)"
              type="number"
              required
              {...register("slot_granularity_minutes")}
              error={errors.slot_granularity_minutes?.message}
            />
            <Field
              label="Min lead time (hours)"
              type="number"
              required
              {...register("min_lead_time_hours")}
              error={errors.min_lead_time_hours?.message}
            />
            <Field
              label="Max lead time (days)"
              type="number"
              required
              {...register("max_lead_time_days")}
              error={errors.max_lead_time_days?.message}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submit.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submit.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
